import { Dropdown, IDropdownStyles, IRefObject, Stack } from "@fluentui/react";
import { Label } from "@fluentui/react-components";
import { IDropdown, IDropdownOption } from "@fluentui/react/lib/Dropdown";
import dayjs from "dayjs";
import duration, { Duration }  from "dayjs/plugin/duration";
import * as React from "react";

dayjs.extend(duration);

export interface ITimePickerProps {
    className?: string;
    disabled?: boolean;
    label?: string;
    required?: boolean;
    value: Duration; // moment.Duration;
    onChanged: (value: Duration) => void;
    componentRef: IRefObject<IDropdown>;
}

export interface ITimePickerState {
    hour: number;
    minute: number;
    ampm: boolean; // AM = false, PM = true
}

export const dropdownStylesMinutes: Partial<IDropdownStyles> = {
    root: {  minWidth: "60px", maxWidth: "100px" },
    label: { display: "none" },
};

export const dropdownStylesAmPm: Partial<IDropdownStyles> = {
    root: { minWidth: "70px", maxWidth: "90px", marginLeft: "4px" },
    label: { display: "none" },
};

const ampmOptions: IDropdownOption[] = [
    { key: 0, text: "AM" },
    { key: 1, text: "PM" },
];

export const TimePicker: React.FunctionComponent<ITimePickerProps> = (props: ITimePickerProps) => {
    // Current Time
    const [currentHours, SetCurrentHours] = React.useState<number>(0);
    const [currentMinutes, SetCurrentMinutes] = React.useState<number>(0);
    const [currentAmpm, SetCurrentAmpm] = React.useState<boolean>(true);

    // Dropdown Options
    const [minutesOptions, SetMinutesOptions] = React.useState<IDropdownOption[]>([]);
    const [hoursOptions, SetHoursOptions] = React.useState<IDropdownOption[]>([]);

    React.useEffect(() => {
        const hoursOps: IDropdownOption[] = [];
        const minutesOps: IDropdownOption[] = [];

        if (hoursOps.length === 0) {
                for (let i = 1; i < 13; i++) {
                    hoursOps.push(
                        {
                            key: i === 12 ? 0 : i,
                            text: i < 10 ? ("0" + i.toString()).toString() : i.toString(),
                        },
                    );
                }
            }

        if (minutesOps.length === 0) {
                for (let i = 0; i < 60; i += 5) {
                    minutesOps.push(
                        {
                            key: i,
                            text: i < 10 ? ("0" + i.toString()).toString() : i.toString(),
                        },
                    );
                }
            }

        SetHoursOptions(hoursOps);
        SetMinutesOptions(minutesOps);
    }, []);

    React.useEffect(() => {
        if (minutesOptions.length !== 0) {
            SetCurrentHours(props.value.hours() % 12);
            SetCurrentMinutes(Math.floor(props.value.minutes() / 5) * 5);
            SetCurrentAmpm(props.value.hours() >= 12);
        }
    }, [minutesOptions]);

    const constructDuration = (hour: number, minute: number, ampm: boolean) => {
        const convertedHours: number = hour + (ampm ? 12 : 0);
        const v = dayjs.duration({ hours: convertedHours, minutes: minute });

        SetCurrentHours(hour);
        SetCurrentMinutes(minute);
        SetCurrentAmpm(ampm);
        return v;
    };

    const onHourChanged = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption | undefined, index?: number | undefined) => {
        const newHour = option?.key as number;
        props.onChanged(constructDuration(newHour, currentMinutes, currentAmpm));
    };

    const onMinuteChanged = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption | undefined, index?: number | undefined) => {
        const newMinute = option?.key as number;
        props.onChanged(constructDuration(currentHours, newMinute, currentAmpm));
    };

    const onAMPMChanged = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption | undefined, index?: number | undefined) => {
        const newAmpm = (option?.key as number) === 1;
        props.onChanged(constructDuration(currentHours, currentMinutes, newAmpm));
    };

    return (
        <div role="group" aria-label={props.label || "Start Time"}>
            {props.label && <Label required={props.required} id="timePickerLabel">{props.label}</Label>}
            <Stack horizontal aria-labelledby={props.label ? "timePickerLabel" : undefined}>
                <span>
                    <Dropdown required
                        disabled={props.disabled}
                        aria-label={"Hour, currently set to " + (currentHours === 0 ? "12" : currentHours < 10 ? "0" + currentHours : currentHours)}
                        options={hoursOptions}
                        selectedKey={currentHours}
                        onChange={onHourChanged}
                    />
                </span>
                <span>
                    <Dropdown required
                        disabled={props.disabled}
                        aria-label={"Minutes, currently set to " + (currentMinutes < 10 ? "0" + currentMinutes : currentMinutes)}
                        options={minutesOptions}
                        selectedKey={currentMinutes}
                        styles={dropdownStylesMinutes}
                        onChange={onMinuteChanged}
                        componentRef={props.componentRef}
                    />
                </span>
                <span>
                    <Dropdown required
                        disabled={props.disabled}
                        aria-label={"AM or PM, currently set to " + (currentAmpm ? "PM" : "AM")}
                        options={ampmOptions}
                        selectedKey={currentAmpm ? 1 : 0}
                        styles={dropdownStylesAmPm}
                        onChange={onAMPMChanged}
                    />
                </span>
            </Stack>
        </div>
    );
};

TimePicker.defaultProps = {
    value: dayjs.duration({ hours: 1 }),
    onChanged: () => { },
    disabled: false,
    className: "",
};
